import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, CodeBlock, Field, Icon, Input, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { formatDate } from "../shared/format";
import type { WidgetOut } from "../shared/types";
import { AllowedDomains } from "./allowed-domains";
import { RotateSecretDialog } from "./rotate-secret-dialog";
import { formatBlockedIps, parseBlockedIps, rateLimit } from "./security-model";
import { SettingSwitch } from "./setting-row";
import { useSaveSecurity } from "./use-widget-studio";

const K = "deployKnowledge.widgetStudio.security";
const DEFAULT_PER_VISITOR = 60;
const DEFAULT_PER_IP = 50;
const RATE_MAX_VISITOR = 500;
const RATE_MAX_IP = 1000;

/** Signing sample (code, not copy): sample tokens last 10 min; the server accepts 15 at most (DEC-011 #3); the widget refreshes them a minute early via getToken. */
const JWT_SAMPLE = `# Python (PyJWT) — on your server
import jwt, time

token = jwt.encode(
    {
        "user_id": "user-0193",
        "role": "member",          # maps to an audience
        "name": "Signed-in user",
        "exp": int(time.time()) + 600,
    },
    ${PRODUCT_NAME.toUpperCase()}_SECRET,
    algorithm="HS256",
)`;

function SecretField({ agentId, widget, disabled }: { agentId: string; widget: WidgetOut; disabled: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const state = widget.identity_secret;
  return (
    <div>
      <span className="label">{t(`${K}.secret`)}</span>
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-xs text-fg-muted">{state.configured ? t(`${K}.secretConfigured`) : t(`${K}.secretMissing`)}</p>
        <Button variant="secondary" size="sm" disabled={disabled} onClick={() => { setOpen(true); }}>
          <Icon name="refresh-cw" />
          {state.configured ? t(`${K}.rotate`) : t(`${K}.generate`)}
        </Button>
      </div>
      <p className="hint">
        {t(`${K}.secretHint`)}
        {state.previous_expires_at ? ` ${t(`${K}.previousExpires`, { date: formatDate(state.previous_expires_at, i18n.language, true) })}` : ""}
      </p>
      <RotateSecretDialog agentId={agentId} configured={state.configured} open={open} onOpenChange={setOpen} />
    </div>
  );
}

export function SecurityPanel({ agentId, widget, disabled }: { agentId: string; widget: WidgetOut; disabled: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const save = useSaveSecurity(agentId);
  const security = widget.security;
  const [domains, setDomains] = useState<string[]>(security.allowed_domains);
  const [identity, setIdentity] = useState(security.identity_verification);
  const [perVisitor, setPerVisitor] = useState(rateLimit(security.rate_limits, "messages_per_visitor_hour", DEFAULT_PER_VISITOR));
  const [perIp, setPerIp] = useState(rateLimit(security.rate_limits, "new_chats_per_ip_day", DEFAULT_PER_IP));
  const [blocked, setBlocked] = useState(formatBlockedIps(security.blocked_ips));
  const parsed = parseBlockedIps(blocked);
  const ratesValid = perVisitor >= 1 && perVisitor <= RATE_MAX_VISITOR && perIp >= 1 && perIp <= RATE_MAX_IP;

  const submit = () => {
    if (!parsed.ok || !ratesValid) return;
    save.mutate(
      { allowed_domains: domains, identity_verification: identity, rate_limits: { messages_per_visitor_hour: perVisitor, new_chats_per_ip_day: perIp }, blocked_ips: parsed.entries },
      { onSuccess: () => toast.success(t(`${K}.saved`)), onError: (error) => toast.error(message(error)) },
    );
  };

  return (
    <div className="space-y-6">
      <AllowedDomains domains={domains} onChange={setDomains} disabled={disabled} />
      <div className="rounded-lg border border-line p-4">
        <SettingSwitch title={t(`${K}.identity`)} hint={t(`${K}.identityHint`)} checked={identity} onCheckedChange={setIdentity} disabled={disabled}>
          <div className="space-y-4">
            <ol className="space-y-1.5 text-xs text-fg-muted">
              {[1, 2, 3].map((n) => (
                <li key={n}>
                  <strong className="text-fg">{n}.</strong> {t(`${K}.step${String(n)}`, { product: PRODUCT_NAME })}
                </li>
              ))}
            </ol>
            <SecretField agentId={agentId} widget={widget} disabled={disabled} />
            <CodeBlock code={JWT_SAMPLE} label={t(`${K}.sample`)} copyLabel={t("common.copy")} copiedLabel={t("common.copied")} />
          </div>
        </SettingSwitch>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t(`${K}.rateVisitor`)} hint={t(`${K}.rateVisitorHint`)}>
          <Input type="number" min={1} max={RATE_MAX_VISITOR} value={perVisitor} disabled={disabled} invalid={perVisitor < 1 || perVisitor > RATE_MAX_VISITOR} onChange={(e) => { setPerVisitor(e.target.valueAsNumber); }} />
        </Field>
        <Field label={t(`${K}.rateIp`)} hint={t(`${K}.rateIpHint`)}>
          <Input type="number" min={1} max={RATE_MAX_IP} value={perIp} disabled={disabled} invalid={perIp < 1 || perIp > RATE_MAX_IP} onChange={(e) => { setPerIp(e.target.valueAsNumber); }} />
        </Field>
      </div>
      <Field label={t(`${K}.blocked`)} hint={t(`${K}.blockedHint`)} error={parsed.ok ? undefined : t(`${K}.blockedInvalid`, { line: parsed.line })}>
        <Textarea rows={3} className="font-mono text-xs" value={blocked} disabled={disabled} onChange={(e) => { setBlocked(e.target.value); }} />
      </Field>
      {disabled ? null : (
        <Button className="w-full" loading={save.isPending} disabled={!parsed.ok || !ratesValid} onClick={submit}>
          <Icon name="shield-check" />
          {t(`${K}.save`)}
        </Button>
      )}
    </div>
  );
}
